import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { GraduationCap, Loader2, Sparkles, CheckCircle2, ArrowRight } from "lucide-react";

const KEYWORDS = [
  "primeiro emprego", "sem experiência", "sem experiencia", "jovem aprendiz", "aprendiz",
  "auxiliar", "assistente", "atendente", "operador", "recepcionista", "estoquista",
  "balconista", "vendedor", "estágio", "estagio",
];

function seemsEntryLevel(vaga) {
  const text = `${vaga.cargo || ""} ${vaga.tipo || ""} ${vaga.descricao || ""}`.toLowerCase();
  return KEYWORDS.some((k) => text.includes(k));
}

export default function PrimeiroEmprego() {
  const [vagas, setVagas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [cidade, setCidade] = useState("");

  useEffect(() => {
    api.get("/vagas")
      .then((r) => setVagas(r.data || []))
      .finally(() => setLoading(false));
  }, []);

  const cidades = useMemo(() => [...new Set(vagas.map((v) => v.cidade).filter(Boolean))].sort(), [vagas]);
  const compativeis = useMemo(
    () => vagas.filter((v) => seemsEntryLevel(v) && (!cidade || v.cidade === cidade)),
    [vagas, cidade]
  );

  return (
    <main className="max-w-7xl mx-auto px-5 py-10 md:py-14">
      <section className="bg-gradient-to-br from-blue-700 to-indigo-900 text-white rounded-3xl p-7 md:p-10">
        <div className="max-w-3xl">
          <span className="inline-flex items-center gap-2 text-blue-100 font-extrabold"><GraduationCap size={20} /> Primeiro Emprego</span>
          <h1 className="font-heading text-4xl md:text-5xl font-extrabold tracking-tight mt-3">Comece sem precisar saber tudo</h1>
          <p className="text-blue-100 text-lg mt-4 leading-relaxed">
            Reunimos oportunidades cujo texto indica perfil de entrada, aprendizagem, estágio ou funções normalmente abertas a quem está começando. Sempre confirme os requisitos no anúncio completo.
          </p>
        </div>
      </section>

      <section className="grid md:grid-cols-3 gap-4 mt-8">
        {[
          ["Currículo curto e claro", "Uma página já pode bastar. Destaque estudo, cursos, projetos, voluntariado e disponibilidade."],
          ["Experiência não é só emprego", "Projetos pessoais, trabalho informal, escola e atividades comunitárias podem mostrar responsabilidade."],
          ["Cuidado com promessas", "Nenhuma empresa séria precisa cobrar para você participar de uma seleção."],
        ].map(([title, text]) => (
          <article key={title} className="bg-white border border-slate-200 rounded-2xl p-5">
            <CheckCircle2 className="text-emerald-600" size={23} />
            <h2 className="font-heading font-extrabold text-lg mt-3">{title}</h2>
            <p className="text-slate-600 mt-2 leading-relaxed">{text}</p>
          </article>
        ))}
      </section>

      <section className="mt-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-6">
          <div>
            <span className="inline-flex items-center gap-2 text-indigo-700 font-extrabold text-sm"><Sparkles size={16} /> Seleção automática por palavras do anúncio</span>
            <h2 className="font-heading text-3xl font-extrabold tracking-tight mt-1">Vagas para quem está começando</h2>
          </div>
          <select value={cidade} onChange={(e) => setCidade(e.target.value)} className="bg-white border border-slate-200 rounded-xl px-4 py-3 min-w-56">
            <option value="">Todas as cidades</option>
            {cidades.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="py-20 flex justify-center text-slate-500"><Loader2 className="animate-spin" /></div>
        ) : compativeis.length === 0 ? (
          <div className="bg-white border border-slate-200 rounded-3xl p-8 text-center">
            <h3 className="font-heading text-xl font-extrabold">Nenhuma vaga identificada agora.</h3>
            <p className="text-slate-600 mt-2">Isso não significa que não existam oportunidades de entrada. Confira também a lista geral.</p>
            <Link to="/" className="inline-flex items-center gap-2 mt-5 bg-slate-950 text-white px-5 py-3 rounded-xl font-extrabold">Ver todas as vagas <ArrowRight size={17} /></Link>
          </div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
            {compativeis.map((vaga) => (
              <Link key={vaga.id} to={`/vaga/${vaga.id}`} className="bg-white border border-slate-200 rounded-2xl p-6 hover:shadow-lg hover:-translate-y-1 transition-all">
                <span className="text-xs font-extrabold bg-blue-100 text-blue-800 rounded-full px-3 py-1">Possível vaga de entrada</span>
                <h3 className="font-heading text-xl font-extrabold mt-4">{vaga.cargo}</h3>
                <p className="text-slate-500 font-bold mt-1">{vaga.company_name} • {vaga.cidade}</p>
                <p className="text-slate-600 mt-4 line-clamp-3">{vaga.descricao}</p>
                <p className="font-extrabold mt-4">Salário: {vaga.salario || "não informado"}</p>
                <span className="inline-block mt-4 text-emerald-700 font-extrabold">Ver requisitos →</span>
              </Link>
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
